import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Button, CircularProgress, Alert, Grid, Chip, Paper, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import axiosInstance from '../../utils/axios';
import { getImageUrl, getImageUrlForPDF, compressImages } from '../../utils/imageUtils';
import { RootState } from '../../store';
import { Task, UserRole } from '../../types';
import SignatureCanvas from 'react-signature-canvas';
import StarIcon from '@mui/icons-material/Star';
import jsPDF from 'jspdf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faStarHalfAlt } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';

const TaskDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [beforeFiles, setBeforeFiles] = useState<File[]>([]);
  const [duringFiles, setDuringFiles] = useState<File[]>([]);
  const [afterFiles, setAfterFiles] = useState<File[]>([]);
  const backendUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:3001';
  const [openEval, setOpenEval] = useState(false);
  const [evaluation, setEvaluation] = useState<{ [key: string]: number }>({});
  const [signature, setSignature] = useState<string>('');
  const sigCanvasRef = useRef<SignatureCanvas>(null);

  useEffect(() => {
    fetchTask();
    // eslint-disable-next-line
  }, [id]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/tasks/${id}`);
      setTask(response.data);
      setError(null);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al cargar la tarea');
    } finally {
      setLoading(false);
    }
  };

  const canUpload = user && (user.role === UserRole.DIRECTOR || (user.role === UserRole.MANAGER && user.id === task?.responsibleId));

  const handlePhotoUpload = async (type: 'before' | 'during' | 'after') => {
    setUploading(true);
    const formData = new FormData();
    const files = type === 'before' ? beforeFiles : type === 'during' ? duringFiles : afterFiles;
    
    // Comprimir imágenes
    const compressedFiles = await compressImages(files);
    compressedFiles.forEach(file => formData.append('photos', file));
    
    formData.append('type', type);
    try {
      await axiosInstance.post(`/tasks/${id}/complete`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBeforeFiles([]);
      setDuringFiles([]);
      setAfterFiles([]);
      fetchTask();
      setError(null);
    } catch (error) {
      setError('Error al subir las fotos');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenEval = () => {
    if (!task?.category?.evaluationFields) return;
    // Inicializar evaluación con 5 estrellas por defecto
    const initialEval: { [key: string]: number } = {};
    task.category.evaluationFields.forEach(f => { initialEval[f] = 5; });
    setEvaluation(initialEval);
    setSignature('');
    setOpenEval(true);
  };

  const handleClearSignature = () => {
    sigCanvasRef.current?.clear();
    setSignature('');
  };

  const handleSaveSignature = () => {
    if (sigCanvasRef.current) {
      setSignature(sigCanvasRef.current.getCanvas().toDataURL('image/png'));
    }
  };

  const handleConfirmComplete = async () => {
    try {
      await axiosInstance.put(`/tasks/${id}/mark-completed`, {
        evaluation,
        signature: signature || sigCanvasRef.current?.getTrimmedCanvas().toDataURL('image/png')
      });
      setOpenEval(false);
      fetchTask();
    } catch (e) {
      setError('Error al completar la tarea');
    }
  };

  const handleDownloadPDF = async () => {
    if (!task) return;
    const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 40;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;

    // Registrar la fuente DejaVuSans para las estrellas
    doc.addFont('/fonts/DejaVuSans.ttf', 'DejaVu', 'normal');

    const addNewPage = () => {
      doc.addPage();
      y = margin;
    };

    // Logo centrado
    const logo = new Image();
    logo.src = '/logo-blanco.jpg';
    await new Promise(resolve => { logo.onload = resolve; });
    const logoWidth = 120;
    const logoHeight = 40;
    doc.addImage(logo, 'JPEG', (pageWidth - logoWidth) / 2, y, logoWidth, logoHeight);
    y += logoHeight + 30;

    // Título centrado
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Informe de Servicio', pageWidth / 2, y, { align: 'center' });
    y += 30;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.text(`Fecha: ${new Date(task.dateTime).toLocaleDateString()}`, margin, y);
    y += 18;
    doc.text(`Empresa: ${task.company?.name || ''}`, margin, y);
    y += 18;
    doc.text(`Responsable: ${task.responsible ? task.responsible.firstName + ' ' + task.responsible.lastName : ''}`, margin, y);
    y += 18;

    // Línea separadora
    doc.setDrawColor(180);
    doc.line(margin, y, pageWidth - margin, y);
    y += 18;

    // Calificaciones
    if (task.evaluation) {
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text('Calificaciones', margin, y);
      y += 20;
      Object.entries(task.evaluation).forEach(([campo, valor]) => {
        if (y > pageHeight - 50) {
          addNewPage();
        }
        doc.setFontSize(13);
        doc.setFont('helvetica', 'normal');
        doc.text(`${campo}:`, margin + 20, y + 10);
        // Cambiar a la fuente DejaVu para las estrellas
        doc.setFont('DejaVu', 'normal');
        doc.setFontSize(16);
        const estrellas = '★'.repeat(valor as number) + '☆'.repeat(5 - (valor as number));
        doc.text(estrellas, margin + 140, y + 10);
        // Volver a la fuente normal
        doc.setFont('helvetica', 'normal');
        y += 28;
      });
      y += 5;
    }

    // Línea separadora
    if (y > pageHeight - 50) {
      addNewPage();
    }
    doc.setDrawColor(180);
    doc.line(margin, y, pageWidth - margin, y);
    y += 18;

    // Fotos Antes
    if (y > pageHeight - 100) {
      addNewPage();
    }
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('Fotos Antes', margin, y);
    y += 18;
    if (task.beforePhoto && task.beforePhoto.length > 0) {
      for (const url of task.beforePhoto) {
        try {
          const imgData = await toDataURL(getImageUrlForPDF(url, backendUrl));
          const tempImg = new window.Image();
          tempImg.src = imgData;
          await new Promise(resolve => { tempImg.onload = resolve; });
          let imgW = tempImg.width;
          let imgH = tempImg.height;
          const maxW = (pageWidth - (margin * 2)) / 2;
          if (imgW > maxW) {
            imgH = imgH * (maxW / imgW);
            imgW = maxW;
          }
          
          if (y + imgH > pageHeight - margin) {
            addNewPage();
          }
          
          doc.addImage(imgData, 'JPEG', margin, y, imgW, imgH);
          y += imgH + 12;
        } catch (e) {
          // Ignorar error de imagen
        }
      }
    }

    // Fotos Durante
    if (y > pageHeight - 100) {
      addNewPage();
    }
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('Fotos Durante', margin, y);
    y += 18;
    if (task.duringPhoto && task.duringPhoto.length > 0) {
      for (const url of task.duringPhoto) {
        try {
          const imgData = await toDataURL(getImageUrlForPDF(url, backendUrl));
          const tempImg = new window.Image();
          tempImg.src = imgData;
          await new Promise(resolve => { tempImg.onload = resolve; });
          let imgW = tempImg.width;
          let imgH = tempImg.height;
          const maxW = (pageWidth - (margin * 2)) / 2;
          if (imgW > maxW) {
            imgH = imgH * (maxW / imgW);
            imgW = maxW;
          }
          
          if (y + imgH > pageHeight - margin) {
            addNewPage();
          }
          
          doc.addImage(imgData, 'JPEG', margin, y, imgW, imgH);
          y += imgH + 12;
        } catch (e) {
          // Ignorar error de imagen
        }
      }
    }

    // Fotos Después
    if (y > pageHeight - 100) {
      addNewPage();
    }
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('Fotos Después', margin, y);
    y += 18;
    if (task.afterPhoto && task.afterPhoto.length > 0) {
      for (const url of task.afterPhoto) {
        try {
          const imgData = await toDataURL(getImageUrlForPDF(url, backendUrl));
          const tempImg = new window.Image();
          tempImg.src = imgData;
          await new Promise(resolve => { tempImg.onload = resolve; });
          let imgW = tempImg.width;
          let imgH = tempImg.height;
          const maxW = (pageWidth - (margin * 2)) / 2;
          if (imgW > maxW) {
            imgH = imgH * (maxW / imgW);
            imgW = maxW;
          }
          
          if (y + imgH > pageHeight - margin) {
            addNewPage();
          }
          
          doc.addImage(imgData, 'JPEG', margin, y, imgW, imgH);
          y += imgH + 12;
        } catch (e) {
          // Ignorar error de imagen
        }
      }
    }

    // Firma
    if (task.signature) {
      if (y > pageHeight - 150) {
        addNewPage();
      }
      y += 10;
      doc.setDrawColor(180);
      doc.line(margin, y, pageWidth - margin, y);
      y += 18;
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text('Firma', pageWidth / 2, y, { align: 'center' });
      y += 10;
      doc.addImage(task.signature, 'PNG', (pageWidth - 200) / 2, y, 200, 60);
      y += 70;
    }

    // Nombre de archivo personalizado
    const fecha = new Date(task.dateTime).toISOString().slice(0, 10);
    const empresa = (task.company?.name || 'empresa').replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(`${fecha}-${empresa}.pdf`);
  };

  function toDataURL(url: string): Promise<string> {
    // La URL ya viene procesada desde getImageUrlForPDFDirect
    console.log('toDataURL - URL to fetch:', url);
    
    return fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .catch(error => {
        console.error('Error loading image for PDF:', error);
        throw error;
      });
  }

  const handleCancel = async () => {
    if (!window.confirm('¿Está seguro que desea cancelar esta tarea? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await axiosInstance.put(`/tasks/${id}/cancel`);
      fetchTask();
      setError(null);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al cancelar la tarea');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!task) return null;

  return (
    <Box maxWidth={900} mx="auto" mt={4}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>Volver</Button>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Detalle de la Tarea</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><b>Nombre de Servicio:</b> {task.location}</Grid>
          <Grid item xs={12} md={6}><b>Fecha y Hora:</b> {new Date(task.dateTime).toLocaleString()}</Grid>
          <Grid item xs={12} md={6}><b>Empresa:</b> {task.company?.name}</Grid>
          <Grid item xs={12} md={6}><b>Responsable:</b> {task.responsible ? `${task.responsible.firstName} ${task.responsible.lastName}` : 'No asignado'}</Grid>
          <Grid item xs={12} md={6}><b>Categoría:</b> {task.category?.name || 'Sin categoría'}</Grid>
          <Grid item xs={12} md={6}><b>Estado:</b> <Chip label={task.status} color={task.status === 'COMPLETED' ? 'success' : task.status === 'IN_PROGRESS' ? 'warning' : task.status === 'CANCELLED' ? 'error' : 'default'} /></Grid>
          {task.notes && <Grid item xs={12}><b>Notas:</b> {task.notes}</Grid>}
          {/* Monto cobrado y gastos */}
          {(task.amount != null || task.gastoEmpleados != null || task.gastoInsumos != null || task.gastoMaquinas != null) && (
            <Grid item xs={12}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mt: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Resumen Financiero</Typography>
                {task.amount != null && (
                  <Box display="flex" justifyContent="space-between"><Typography>Monto cobrado:</Typography><Typography fontWeight={500}>${task.amount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</Typography></Box>
                )}
                {task.gastoEmpleados != null && (
                  <Box display="flex" justifyContent="space-between"><Typography>- Empleados:</Typography><Typography color="error.main">${task.gastoEmpleados.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</Typography></Box>
                )}
                {task.gastoInsumos != null && (
                  <Box display="flex" justifyContent="space-between"><Typography>- Insumos:</Typography><Typography color="error.main">${task.gastoInsumos.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</Typography></Box>
                )}
                {task.gastoMaquinas != null && (
                  <Box display="flex" justifyContent="space-between"><Typography>- Alquiler de Máquinas:</Typography><Typography color="error.main">${task.gastoMaquinas.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</Typography></Box>
                )}
                {task.amount != null && (task.gastoEmpleados != null || task.gastoInsumos != null || task.gastoMaquinas != null) && (() => {
                  const totalGastos = (task.gastoEmpleados ?? 0) + (task.gastoInsumos ?? 0) + (task.gastoMaquinas ?? 0);
                  const neto = task.amount - totalGastos;
                  return (
                    <Box display="flex" justifyContent="space-between" sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography fontWeight="bold">Neto:</Typography>
                      <Typography fontWeight="bold" color={neto >= 0 ? 'success.main' : 'error.main'}>${neto.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</Typography>
                    </Box>
                  );
                })()}
              </Box>
            </Grid>
          )}
        </Grid>
        <Box mt={4}>
          <Typography variant="h6">Fotos Antes</Typography>
          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            {task.beforePhoto && task.beforePhoto.length > 0 && task.beforePhoto.map((url, idx) => (
              <img key={idx} src={getImageUrl(url, backendUrl)} alt="Antes" style={{ width: 150, height: 150, objectFit: 'cover' }} />
            ))}
          </Box>
          {canUpload && task.status !== 'COMPLETED' && (
            <Box mb={2}>
              <input type="file" multiple accept="image/*" style={{ display: 'none' }} id="before-upload" onChange={e => setBeforeFiles(Array.from(e.target.files || []))} />
              <label htmlFor="before-upload">
                <Button variant="outlined" component="span" disabled={uploading}>Seleccionar fotos (Antes)</Button>
              </label>
              <Box mt={1}>
                <div
                  style={{ border: '2px dashed #aaa', padding: 16, borderRadius: 8, minHeight: 60, cursor: 'pointer' }}
                  onDrop={e => { e.preventDefault(); setBeforeFiles(Array.from(e.dataTransfer.files)); }}
                  onDragOver={e => e.preventDefault()}
                >
                  {beforeFiles.length > 0 ? beforeFiles.map(f => f.name).join(', ') : 'Arrastra aquí las fotos de antes'}
                </div>
              </Box>
              <Button variant="contained" color="primary" sx={{ mt: 1 }} disabled={uploading || beforeFiles.length === 0} onClick={() => handlePhotoUpload('before')}>Subir Fotos Antes</Button>
            </Box>
          )}
        </Box>
        <Box mt={4}>
          <Typography variant="h6">Fotos Durante</Typography>
          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            {task.duringPhoto && task.duringPhoto.length > 0 && task.duringPhoto.map((url, idx) => (
              <img key={idx} src={getImageUrl(url, backendUrl)} alt="Durante" style={{ width: 150, height: 150, objectFit: 'cover' }} />
            ))}
          </Box>
          {canUpload && task.status !== 'COMPLETED' && (
            <Box mb={2}>
              <input type="file" multiple accept="image/*" style={{ display: 'none' }} id="during-upload" onChange={e => setDuringFiles(Array.from(e.target.files || []))} />
              <label htmlFor="during-upload">
                <Button variant="outlined" component="span" disabled={uploading}>Seleccionar fotos (Durante)</Button>
              </label>
              <Box mt={1}>
                <div
                  style={{ border: '2px dashed #aaa', padding: 16, borderRadius: 8, minHeight: 60, cursor: 'pointer' }}
                  onDrop={e => { e.preventDefault(); setDuringFiles(Array.from(e.dataTransfer.files)); }}
                  onDragOver={e => e.preventDefault()}
                >
                  {duringFiles.length > 0 ? duringFiles.map(f => f.name).join(', ') : 'Arrastra aquí las fotos durante'}
                </div>
              </Box>
              <Button variant="contained" color="primary" sx={{ mt: 1 }} disabled={uploading || duringFiles.length === 0} onClick={() => handlePhotoUpload('during')}>Subir Fotos Durante</Button>
            </Box>
          )}
        </Box>
        <Box mt={4}>
          <Typography variant="h6">Fotos Después</Typography>
          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            {task.afterPhoto && task.afterPhoto.length > 0 && task.afterPhoto.map((url, idx) => (
              <img key={idx} src={getImageUrl(url, backendUrl)} alt="Después" style={{ width: 150, height: 150, objectFit: 'cover' }} />
            ))}
          </Box>
          {canUpload && task.status !== 'COMPLETED' && (
            <Box mb={2}>
              <input type="file" multiple accept="image/*" style={{ display: 'none' }} id="after-upload" onChange={e => setAfterFiles(Array.from(e.target.files || []))} />
              <label htmlFor="after-upload">
                <Button variant="outlined" component="span" disabled={uploading}>Seleccionar fotos (Después)</Button>
              </label>
              <Box mt={1}>
                <div
                  style={{ border: '2px dashed #aaa', padding: 16, borderRadius: 8, minHeight: 60, cursor: 'pointer' }}
                  onDrop={e => { e.preventDefault(); setAfterFiles(Array.from(e.dataTransfer.files)); }}
                  onDragOver={e => e.preventDefault()}
                >
                  {afterFiles.length > 0 ? afterFiles.map(f => f.name).join(', ') : 'Arrastra aquí las fotos de después'}
                </div>
              </Box>
              <Button variant="contained" color="primary" sx={{ mt: 1 }} disabled={uploading || afterFiles.length === 0} onClick={() => handlePhotoUpload('after')}>Subir Fotos Después</Button>
            </Box>
          )}
        </Box>
        {task.signature && (
          <Box mt={4} textAlign="center">
            <Typography variant="h6">Firma</Typography>
            <img src={task.signature} alt="Firma" style={{ maxWidth: 400, border: '1px solid #ccc', borderRadius: 8 }} />
            <Box mt={2}>
              <Button variant="outlined" color="primary" onClick={handleDownloadPDF}>
                Descargar Informe de servicio (PDF)
              </Button>
            </Box>
          </Box>
        )}
        {canUpload && task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && (
          <Button
            variant="contained"
            color="success"
            sx={{ mt: 2 }}
            disabled={!(task.beforePhoto && task.beforePhoto.length > 0 && task.afterPhoto && task.afterPhoto.length > 0)}
            onClick={handleOpenEval}
          >
            Completar tarea
          </Button>
        )}
        {user?.role === UserRole.DIRECTOR && task.status !== 'CANCELLED' && task.status !== 'COMPLETED' && (
          <Button
            variant="contained"
            color="error"
            sx={{ mt: 2, ml: 2 }}
            onClick={handleCancel}
          >
            Anular tarea
          </Button>
        )}
        <Button
          variant="contained"
          color="error"
          sx={{ mt: 2, ml: 1 }}
          onClick={handleCancel}
        >
          Cancelar tarea
        </Button>
      </Paper>
      <Dialog open={openEval} onClose={() => setOpenEval(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Calificar y firmar tarea</DialogTitle>
        <DialogContent>
          {task?.category?.evaluationFields?.map((field) => (
            <Box key={field} mb={2}>
              <Typography>{field}</Typography>
              {[1,2,3,4,5].map(star => (
                <StarIcon
                  key={star}
                  sx={{ color: evaluation[field] >= star ? '#FFD700' : '#ccc', cursor: 'pointer' }}
                  onClick={() => setEvaluation(ev => ({ ...ev, [field]: star }))}
                />
              ))}
            </Box>
          ))}
          <Box mt={3}>
            <Typography>Firma</Typography>
            <SignatureCanvas
              ref={sigCanvasRef}
              penColor="black"
              canvasProps={{ width: 400, height: 120, className: 'sigCanvas', style: { border: '1px solid #ccc', borderRadius: 8 } }}
              onEnd={handleSaveSignature}
            />
            <Box mt={1} display="flex" gap={2}>
              <Button onClick={handleClearSignature} variant="outlined">Borrar firma</Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEval(false)}>Cancelar</Button>
          <Button
            onClick={handleConfirmComplete}
            variant="contained"
            color="success"
            disabled={Object.values(evaluation).some(v => !v) || !sigCanvasRef.current || sigCanvasRef.current.isEmpty()}
          >
            Confirmar y completar tarea
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskDetailPage;